/**
 * Live Firestore subscription for an in-flight Tripo mesh job.
 *
 * The backend `processMeshJob` Firestore trigger writes progress updates to
 * `conversations/{conversationId}/meshJobs/{taskId}` as it polls Tripo. The
 * editor uses this hook to render a progress bar and surface terminal
 * failures. When either arg is missing or the doc does not exist yet, the
 * hook returns `null`.
 */

import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { firestore } from '@/lib/firebase';

export type MeshJobStatus =
  | 'queued'
  | 'running'
  | 'success'
  | 'failed'
  | 'banned'
  | 'expired'
  | 'cancelled'
  | 'unknown';

export interface MeshJobProgress {
  status: MeshJobStatus;
  progress: number;
  errorCode?: number;
  errorMsg?: string;
}

interface Args {
  conversationId: string | undefined;
  taskId: string | undefined;
}

export function useMeshJobProgress(args: Args): MeshJobProgress | null {
  const [data, setData] = useState<MeshJobProgress | null>(null);
  useEffect(() => {
    if (!args.conversationId || !args.taskId) {
      setData(null);
      return;
    }
    const ref = doc(
      firestore,
      'conversations',
      args.conversationId,
      'meshJobs',
      args.taskId,
    );
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) {
        setData(null);
        return;
      }
      const d = snap.data() as Partial<MeshJobProgress> | undefined;
      if (!d || !d.status) {
        setData(null);
        return;
      }
      setData({
        status: d.status,
        progress: d.progress ?? 0,
        errorCode: d.errorCode,
        errorMsg: d.errorMsg,
      });
    });
    return unsub;
  }, [args.conversationId, args.taskId]);
  return data;
}

export const TERMINAL_FAILURE_STATUSES: MeshJobStatus[] = [
  'failed',
  'banned',
  'expired',
  'cancelled',
  'unknown',
];

export function isTerminalFailure(status: MeshJobStatus): boolean {
  return TERMINAL_FAILURE_STATUSES.includes(status);
}
